<template>
  <div id="app">
    <main-svg ref="logoSVG" tabindex="0" role="img" :convertedWindowSize="convertedWindowSize"/>
    <div class="body-content">
      <div class="section-2-3">
        <section class="section-1 purple" tabindex="0" tabTrigger="section1" @focus="onFocus($event.target)">
          <h2 style="opacity: 1; visibility: visible; position: absolute">Hi, I'm Cailtin</h2>
        </section>  
        <section class="section-2 salmon">
          <div class="width-container">
            <h2>I want to...</h2>
            <div class="transition">
              <h2 v-for="item, index in content.aboutMe" 
                  :key="item.id" 
                  tabindex="0" 
                  tabTrigger="section2"
                  @focus="onFocus($event.target, index)">{{item}}</h2>
            </div>
          </div>
        </section>
      
      
      </div>
      <section class="section-3">
        <projects />
      </section>
      <section class="section-4 teal">
      </section>
      <section class="section-5 periwinkle">
        <h2>Let's keep in touch 😏</h2>
        <p><a class="email" href="mailto:caitlinmgleason@gmail.com" target="_blank">caitlin<span>.</span>m<span>.</span>gleason@gmail<span>.</span>com</a></p>
        <social-links/>
      </section>
    </div>
  </div>
</template>

<script>

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger.js";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin.js";
import MainSvg from './components/MainSvg.vue'
import Projects from './components/Projects.vue'
import SocialLinks from './components/SocialLinks.vue'

export default {
  name: 'App',
  components: {
    MainSvg,
    Projects,
    SocialLinks
  },
  data() {
    return {
      content: {
        aboutMe: [
          "Bring fun websites back",
          "Tell stories with pixels",
          "Promote accessibility for all!"
        ]
      },
      debounceTimer: null,
      convertedWindowSize: null,
      fullScreenOpen: false
    }
  },
  methods: {
    onFocus(target, index) {
      if (!index) { index = 0 }
        let trigger = ScrollTrigger.getById(target.getAttribute("tabTrigger"));
        let triggerLength = trigger.end - trigger.start;
        let offsets=[
          (triggerLength/6),
          (triggerLength/6)*3,
          (triggerLength/6)*5,
        ]
        gsap.to(window, {duration: .25, scrollTo: {y: trigger.start + offsets[index]}});
    },
    introAnim() {
      gsap.set("#body-path", {autoAlpha: 0})
      let getPercentage = ()=> {
        return Math.floor(DrawSVGPlugin.getPosition("#margin-bottom")[1] / (DrawSVGPlugin.getLength("#margin-bottom") / 100))*.01
      };
      //quickly scrubs to end of animation if user doesn't wait for animation to complete before scrolling
      gsap.to(this.$refs.logoSVG.introTl, {
        progress: 1,
        scrollTrigger: {
          id: "introScrub",
          trigger: ".hero",
          start: "bottom bottom",
          end: "bottom 80%",
          scrub: true,
          once: true,
        }
      })
      let intro = gsap.timeline({
        scrollTrigger: {
          id: "intro", 
          //markers: true,
          trigger: ".hero",
          start: "bottom 80%",
          endTrigger: ".section-1",
          end: "bottom bottom",
          scrub: true, 
          invalidateOnRefresh: true,
          onEnter: ()=>{ 
            ScrollTrigger.getById("intro").refresh()
            gsap.getById("bounce").pause();
          },
          onLeaveBack: ()=>{gsap.getById("bounce").play();}
        }
      });
      intro.to(".svg-container #margin-bottom", {drawSVG: ()=> '100%', ease: "sine.in"})
            .to(".svg-container .body-target", {ease: "sine.in",
              motionPath: {
                path: ".svg-container #margin-bottom",
                align: ".svg-container #margin-bottom", 
                start: ()=> getPercentage(),
                alignOrigin: [0.5, 0.5]
            }}, "<")
            .set("#body-path", {autoAlpha: 1})
            .fromTo("#body-path", {drawSVG: '0%'}, {drawSVG: ()=> '3.5%', ease: "sine.out"})
            .to(".svg-container .body-target", {ease: "sine.out",
            motionPath: {
              path: "#body-path",
              end: ()=> .035,
              align: "#body-path", 
              alignOrigin: [0.5, 0.5]
          }}, "<")
        return intro;
    },
    section1(){
      let periodAnim = gsap.timeline({paused: true})
          periodAnim.to(".intro-period", {duration: .5, ease: "circ.out", attr: {r: 4}}, "start")
              .to(".intro-period", {duration: 3.25, ease: "linear", 
                  motionPath: {
                    path: ".intro-period-path",
                    align: ".intro-period-path",
                    alignOrigin: [0.5, 0.5]
                  }}, "<")
              .to(".intro-period", {y: "+=5", duration: .15, ease: "linear"})
              .to(".intro-period", {y: "-=5", duration: .35, ease: "back.in"})
              .from(".intro-dot-1", {y: "+=20", duration: .5, ease: "back.out(3)"}, "start+=1.5")
              .to(".intro-dot-1", {attr: {r: 3.75}, duration: .2}, "<")
              .from(".intro-dot-2", {y: "+=20", duration: .5, ease: "back.out(3)"}, "start+=2.5")
              .to(".intro-dot-2", {attr: {r: 3.75}, duration: .2}, "<")
              .from(".intro-dot-3", {y: "+=20", duration: .5, ease: "back.out(3)"}, "start+=2.8")
              .to(".intro-dot-3", {attr: {r: 3.75}, duration: .2}, "<")

      let config = {
        trigger: ".section-1",
        start: "center center",
        end: "bottom -30%",
        pinnedContainer: ".section-2-3",
        anticipatePin: 1,
        //markers: true
      }
      ScrollTrigger.create({
        pinSpacing: false,
        id: "section1pin1",
        pin: '.svg-container',
        ...config
      })
      ScrollTrigger.create({
        pinSpacing: true,
        id: "section1pin2",
        pin: ".section-1",
        ...config
      })
      let section1 = gsap.timeline({
        scrollTrigger: {
          //markers: true,
          id: "section1", 
          trigger: ".section-1",
          start: "center center",
          end: "bottom -30%",
          scrub: true,
          invalidateOnRefresh: true,
          //pinnedContainer: ".section-2-3",
          onEnter: ()=> periodAnim.timeScale(1.5).restart()
        }
      });
      
      section1.fromTo("#body-path", {drawSVG: ()=> '3.5%'}, {drawSVG: ()=> '28%', ease: "linear"})
              .to(".svg-container .body-target", {
                    ease: "linear", 
                    motionPath: {
                      path: "#body-path",
                      start: ()=> .035,
                      end: ()=> .28,
                      align: "#body-path", 
                      alignOrigin: [0.5, 0.5]
                    }}, "<")
      return section1;
    },
    section2Transition() {
      let section2Transition = gsap.timeline({
          scrollTrigger: {
            //markers: true,
            id: "section2Transition",
            trigger: ".section-2",
            start: "top bottom",
            end: "top 15%",
            scrub: true,
            invalidateOnRefresh: true,
            onUpdate: ()=> {
              let target = document.querySelector(".svg-container .n .pink-target");
              let section23 = document.querySelector(".section-2-3");
              if (!this.isOverlapping(target, section23) && !target.classList.contains("hidden")) { 
                gsap.set(target, {opacity: 0})
              } else {
                gsap.set(target, {opacity: 1})
              }
            }
            //onEnter: ()=> {if (!this.detailsOpen) gsap.set("body", {backgroundColor: "#ffb0f4"})},
            //onLeaveBack: ()=> { if (!this.detailsOpen) gsap.set("body", {backgroundColor: "#2f2068"})}
          }
        });
        section2Transition.fromTo("#body-path", {drawSVG: ()=> '28%'}, {drawSVG: ()=> '40%', ease: "linear"})
                .to(".svg-container .body-target", {
                      ease: "linear", 
                      motionPath: {
                        path: "#body-path",
                        start: ()=> .28,
                        end: ()=> .40,
                        align: "#body-path", 
                        alignOrigin: [0.5, 0.5]
                      }}, "<")
        return section2Transition;
    },
    section2(){
        let config = {
          //id: "section2pin1",
          //markers: true,
          anticipatePin: 1,
          trigger: ".section-2",
          start: "top 15%",
          end: ()=> `+=${window.innerHeight * 2.5 }px`
        }
        ScrollTrigger.create({
          id: "section2pin1",
          pinSpacing: false,
          pin: '.svg-container',
          ...config
        })
        ScrollTrigger.create({
          id: "section2pin2",
          pinSpacing: true,
          pin: ".section-2-3",
          ...config
        })
        let section2 = gsap.timeline({
          id: "section2",
          scrollTrigger: {
            //markers: true,
            id: "section2",
            trigger: ".section-2",
            endTrigger: ".section-3",
            start: "top 15%",
            end: "top top",
            scrub: true,
          }
        });
        
        section2.fromTo("#body-path", {drawSVG: '40%'}, {drawSVG: '69%', ease: "linear", duration: 4.5}, "start")
        section2.to(".svg-container .body-target", {
                      ease: "linear", 
                      duration: 4.5,
                      motionPath: {
                        path: "#body-path",
                        start: ()=> .4,
                        end: ()=> .69,
                        align: "#body-path", 
                        alignOrigin: [0.5, 0.5]
                      }}, "<")
                .fromTo(".section-2 .transition h2:first-child", {opacity: 1, y: 0}, {opacity: 0, duration: .5, y: 35}, "start+=1")
                .fromTo(".section-2 .transition h2:nth-child(2)", {opacity: 0, y: -25}, {opacity: 1, y: 0,  duration: .5}, ">-=.2")
                .to(".section-2 .transition h2:nth-child(2)", {opacity: 0, y: 35, duration: .5}, ">+=1")
                .fromTo(".section-2 .transition h2:last-child", {opacity: 0, y: -25}, {opacity: 1, y: 1, duration: .5}, ">-=.2")
        return section2;

    },
    section3() {
      let bounce2 = gsap.timeline({paused: true, id: "bounce2" });
      bounce2.to(".svg-container #body-path", {drawSVG: "100%", repeat: -1, yoyo: true, duration: .5, ease: "circ.out"});
      bounce2.to(".svg-container .body-target", {duration: .5, ease: "circ.out", immediateRender: false, yoyo: true, repeat: -1,
      motionPath: {
                  path: ".svg-container #body-path",
                  start: .98,
                  end: 1,
                  align: ".svg-container #body-path", 
                  alignOrigin: [0.5, 0.5]
                }}, "<");
      let section3 = gsap.timeline({
          id: "detailsSection",
          scrollTrigger: {
            trigger: ".section-2-3",
            start: "bottom top",
            endTrigger: ".section-5",
            end: "bottom bottom+=1",
            scrub: true,
            invalidateOnRefresh: true,
            onLeave: ()=> { bounce2.play(); },
            onEnterBack: ()=> { bounce2.pause().progress(0); }
          },
        });
        section3.fromTo("#body-path", {drawSVG: ()=> '69%'}, {drawSVG: ()=> '98%', ease: "linear"})
        section3.to(".svg-container .body-target", {
                      ease: "linear",
                      motionPath: {
                        path: "#body-path",
                        start: ()=> .69,
                        end: ()=> .98,
                        align: "#body-path", 
                        alignOrigin: [0.5, 0.5]
                      }}, "<")
        return section3;
    },
    initializeBodyTimelines() {
      this.section1();
      this.section2Transition();
      this.section2();
      this.section3();
    }
  },
  mounted() {
    document.fonts.ready.then(() => { 
      this.convertedWindowSize = this.SVGBoundingClientRect(document.querySelector(".hero"));
      ScrollTrigger.normalizeScroll(true)
      ScrollTrigger.config({ 
        pinSpacing: false,
        autoRefreshEvents: "visibilitychange,DOMContentLoaded,load"
      });
      gsap.set("#margin-bottom", {drawSVG: "0%"});
      this.introAnim();
      this.initializeBodyTimelines();

      window.addEventListener("resize", ()=>{
        //prevent refresh when transitioning to fullscreen
        if(!document.fullscreenElement && !this.fullScreenOpen) {
          //disable all triggers
          ScrollTrigger.getAll().forEach((trigger)=>{ trigger.disable()})
          this.$nextTick(()=> {
            let activeItem = document.querySelector("li.active");
            this.convertedWindowSize = this.SVGBoundingClientRect(document.querySelector(".hero"));

            if(this.debounceTimer) { clearTimeout(this.debounceTimer); }
            this.debounceTimer = setTimeout(()=> {
                this.$root.$emit('beforeRefresh', activeItem);
                this.$refs.logoSVG.introTl.kill();
                gsap.getById("bounce").kill();
                this.$refs.logoSVG.introTl = this.$refs.logoSVG.setupIntroTimeline().progress(1);
                gsap.getById("bounce2").pause().invalidate();
                //ScrollTrigger.refresh()
                this.debounceTimer = null;
            },300);
          });
        }
      })
      ScrollTrigger.addEventListener("refresh", ()=> {
        ScrollTrigger.getAll().forEach((trigger)=>{
          if (trigger.animation) { trigger.animation.invalidate(); }
          trigger.enable() 
        })
      });
      document.addEventListener('fullscreenchange', ()=> {
        if(this.fullScreenOpen) {setTimeout(()=> {this.fullScreenOpen = false;}, 500)}
        else { this.fullScreenOpen = true; }
      })
    })
  }
  
}
</script>

<style lang="less" >
[data-whatintent='keyboard'] {
  *:focus { outline: none; }
  *:focus-visible { outline: none; }
}
body {
    overflow-x: hidden;
    width: 100vw;
    margin: 0px;
    #app { overflow: hidden}
}

  .purple {  
     svg & {fill: #2f2068}
     background-color: #2f2068
  }
  .salmon {  
     svg & {fill: #ff779c}
     background-color: #ff779c; 
  }
  .pink { 
    svg & {fill: #ffb0f4}
    background-color: #ffb0f4; 
  }
  .teal{  
    svg & {fill: #9fffee}
    background-color: #9fffee; 
  }
  .periwinkle {  
    svg & {fill: #a6a6ff}
    background-color: #a6a6ff; 
  }
  .pin-spacer-section1pin1, .pin-spacer-section1pin2 { background-color: #2f2068;}
  body {
    background-color: #ffb0f4;
    font-family: "Poppins", Helvetica, sans-serif;
    font-weight: 400;
    font-size: 10px;
    @media (min-width: 400px) {
      font-size: 12px;
    }
    @media (min-width: 650px) {
      font-size: 14px;
    }
    @media (min-width: 1000px) {
      font-size: 16px;
    }
    @media (min-width: 1400px) {
      font-size: 18px;
    }
  }
  &.lock-scroll {
    overflow-y: hidden;
    height: 100vh;
  }
  .width-container {
    margin: 0 auto;
    position: relative;
    width: 80vw;
    @media (min-width: 650px) { width: 70vw; }
    @media (min-width: 800px) { width: 55vw; }
    @media (min-width: 1000px) { width: 48vw; }
    @media (min-width: 1400px) { width: 43vw; max-width: 800px; }
  }
  section {
    position: relative;
    z-index: 1;
    width: 100%;
  }
  h2 {
    color: #2f2068;
    font-family: 'Righteous'; 
    font-weight: 400;
    margin: 0;
    font-size: 2.8em;
    @media (min-width: 400px) { font-size: 2.3em; }
    @media (min-width: 800px) { font-size: 2.5em; }
    @media (min-width: 1200px) { font-size: 2.7em; }
  }
  p a { color: inherit; }
  .section-1 { height: 60vh; background: #2f2068; pointer-events: none;}
  .section-2 { padding-top: 10vw; padding-bottom: 10vw;}
  .section-3 { padding-top: 40vw; padding-bottom: 15vw; z-index: 5000}
  .section-4 { height: 3vw; }
  .section-5 { padding-top: 20vw; padding-bottom: 15vw; }
  @media (min-width: 800px) {
    .section-1 { height: 100vh; }
    .section-3 { padding-top: 30vw; padding-bottom: 7vw;}
    .section-5 { padding-top: 15vw; padding-bottom: 5vw; }
  }
  @media (min-width: 1000px) {
    .section-3 { padding-top: 25vw;}
    .section-2 { padding-top: 7vw; padding-bottom: 7vw;}
  }
  @media (min-width: 1200px) {
    .section-3 { padding-top: 22vw;}
    .section-4 { height: 4vw; }
    .section-5 { padding-top: 10vw;}
  }
  @media (min-width: 1400px) {
    .section-2 { padding-top: 5vw; padding-bottom: 5vw;}
    .section-3 { padding-bottom: 8vw; }
    .section-4 { height: 3vw; }
  }
  @media (min-width: 1600px) {
    .section-3 { padding-top: 25vw; padding-bottom: 100px; }
    .section-4 { height: 50px; }
  }
  @media (min-width: 1900px) {
    .section-2 { padding: 95px 0px; }
    .section-3 { padding-top: 450px; }
    .section-5 { padding-top: 190px; padding-bottom: 95px;}
  }
  .body-content {
    section {
      position: relative;
    }
    position: relative;
    overflow: visible;
    .section-2 {
      .width-container {
        @media (max-width: 399px) { transform:translateX(-5%) translateZ(0)}
        @media (min-width: 400px) and (max-width: 500px) { font-size: 11px; }
        @media (min-width: 800px) { transform: translateX(-15%) translateZ(0); }
        @media (min-width: 1200px) { transform: translateX(-10%) translateZ(0); }
        @media (min-width: 1400px) { transform: translateX(-20%) translateZ(0); }
        @media (min-width: 1700px) { transform: none; }
      }
      .transition {
        position: relative;
        h2 {
          @media (min-width: 800px) {
            white-space: nowrap;
          }
          &:nth-child(1) {color: #5934B4}
          &:nth-child(2) {color: #422786}
          &:nth-child(3){ color: #2f2068}
          &:not(:first-child) {
            position: absolute;
            top: 0;
            left: 0;
            opacity: 0;
          }
        }
      }
    }
    .section-3 {
      @media (min-width: 800px) {
        h2 {width: 120%;}
      }
    }
    .section-5 {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      z-index: auto;
      a {
        display: block;
        z-index: 5001;//to prevent ul from covering links when long projects are open
        position: relative;
        &.email {
          outline: none;
          font-size: 1.6em;
          line-height: 1em;
          text-decoration: underline #8447ff;
          text-decoration-thickness: 2px;
          @media (min-width: 800px) { font-size: 1.3em; }
          span { color:#ff779c; }
        }
      }
    }
  }
</style>
